import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const WashingtonRequirementsPage = () => {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-teal-50 via-white to-gray-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <i className="ri-government-line"></i>
                State Compliance
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Washington <span className="text-teal-600">Cybersecurity Requirements</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Understanding Washington's data security and breach notification requirements, including specific obligations for insurance licensees.
              </p>
            </div>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="py-8 bg-yellow-50 border-y border-yellow-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-start gap-4">
                <i className="ri-information-line text-3xl text-yellow-600 flex-shrink-0"></i>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Important Disclaimer</h3>
                  <p className="text-gray-700 text-sm">
                    The information provided on this page is for general informational purposes only. State cybersecurity regulations are complex and subject to change. We recommend reaching out to our team to confirm you have covered all applicable requirements for Washington operations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="space-y-8">
                {/* Overview */}
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Overview</h2>
                  <p className="text-gray-700 text-lg">
                    Washington has several data security and breach rules. For insurance licensees, the <strong>Office of the Insurance Commissioner (OIC)</strong> enforces specific security breach notification obligations, including under WAC 284-04-625 and related guidance. More broadly, Washington has breach notification laws applicable to many businesses that handle personal information of Washington residents.
                  </p>
                </div>

                {/* Who is Covered */}
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Who is Covered (Insurance Focus)?</h2>
                  <div className="bg-teal-50 rounded-lg p-6 border-l-4 border-teal-600">
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-fill text-teal-600 flex-shrink-0 mt-1 text-xl"></i>
                        <span>Insurers, producers, and other licensees regulated by the Washington OIC</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-fill text-teal-600 flex-shrink-0 mt-1 text-xl"></i>
                        <span>These entities must protect "nonpublic personal financial information" and private consumer information and follow specific breach reporting procedures</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Key Expectations */}
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Key Cybersecurity and Privacy Expectations</h2>
                  
                  <div className="space-y-6">
                    <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <i className="ri-shield-check-line text-teal-600"></i>
                        Safeguarding Consumer Information
                      </h3>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-teal-600 flex-shrink-0 mt-1"></i>
                          <span>Licensees must adopt administrative, technical, and physical safeguards appropriate to the sensitivity of consumer information they handle, consistent with privacy and security obligations under both state and federal law</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <i className="ri-team-line text-teal-600"></i>
                        Vendor and Third-Party Oversight
                      </h3>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-teal-600 flex-shrink-0 mt-1"></i>
                          <span>As in other states, insurers and licensees are expected to ensure that service providers maintain appropriate safeguards for consumer information</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Breach Notification */}
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Security Breach Notification – Insurance Entities</h2>
                  <p className="text-gray-700 text-lg mb-6">
                    If a security breach involves private consumer information (such as Social Security numbers or comparable identifiers) and is reasonably likely to expose customers to a risk of criminal activity, insurers and producers must:
                  </p>
                  <div className="bg-red-50 rounded-lg p-6 border-l-4 border-red-600">
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start gap-3">
                        <i className="ri-alert-line text-red-600 flex-shrink-0 mt-1 text-xl"></i>
                        <span>Notify the Insurance Commissioner in writing within two business days after determining that notification to consumers must be sent</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-alert-line text-red-600 flex-shrink-0 mt-1 text-xl"></i>
                        <div>
                          <span className="font-semibold">Provide details such as:</span>
                          <ul className="mt-2 space-y-2 ml-4">
                            <li className="flex items-start gap-2">
                              <i className="ri-arrow-right-s-line text-red-600 flex-shrink-0 mt-0.5"></i>
                              <span>Number of consumers affected or potentially affected</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-arrow-right-s-line text-red-600 flex-shrink-0 mt-0.5"></i>
                              <span>Nature of the information involved</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-arrow-right-s-line text-red-600 flex-shrink-0 mt-0.5"></i>
                              <span>Steps the licensee is taking to remediate and protect consumers</span>
                            </li>
                          </ul>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-alert-line text-red-600 flex-shrink-0 mt-1 text-xl"></i>
                        <span>Licensees must also comply with Washington's general data breach notification statute, which requires notification to affected residents and, for larger incidents, the Attorney General</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-br from-gray-50 to-teal-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Need Help with Washington Compliance?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Our team helps insurance entities and businesses understand and implement the controls needed to meet Washington's data security and breach notification requirements.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <a href="/schedule-call" className="inline-block">
                  <button className="inline-flex items-center px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors shadow-md whitespace-nowrap cursor-pointer">
                    <i className="ri-calendar-line mr-2"></i>
                    Schedule Consultation
                  </button>
                </a>
                <a href="/risk-assessment" className="inline-block">
                  <button className="inline-flex items-center px-6 py-3 bg-white text-teal-600 font-semibold rounded-lg hover:bg-gray-50 transition-colors border-2 border-teal-600 whitespace-nowrap cursor-pointer">
                    <i className="ri-shield-check-line mr-2"></i>
                    Get Compliance Assessment
                  </button>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default WashingtonRequirementsPage;
